import cloneDeep from 'lodash/cloneDeep';

import { getPage } from '../api/build-a-dash-api';
import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
} from '../components/PageConfigProvider/constants';
import type { TabContent } from '../components/PageConfigProvider/page-reducer';
import {
  type Owner,
  type Page,
  PageType,
} from '../components/PageConfigProvider/types';
import type { PositionedItem } from '../components/PageConfigProvider/types/item';
import { toReadableFilterName } from '../components/Reports/Parameters/parameters';
import { FilterId, type PrimaryFilterId } from '../filters/types/filters';
import { getReportDefaultItems } from '../templates/report-default-items';
import { getLocalReportTemplate } from '../templates/report-templates-registry';
import { generateUniqueTitle } from './generate-unique-title';
import { getLocalTemplate } from './page-config-lookup';
import { type EntityType, entityTypeToFilterId } from './routing-helpers';

interface GeneratePageDraftParams {
  owner: { id: string; username: string };
  existingPages: Page[];
  entityType?: EntityType;
  templateId?: string;
  action?: 'clone';
  clientName?: string;
}

export const getOriginalPage = async ({
  id,
  existingPages,
}: {
  id: string;
  existingPages: Page[];
}): Promise<Page | undefined> => {
  const localTemplatePage = getLocalTemplate(id);
  if (localTemplatePage) return localTemplatePage;

  const existingPage = existingPages.find((p) => p.id === id);
  if (existingPage) return existingPage;

  const reportTemplate = getLocalReportTemplate(id);
  if (reportTemplate) return reportTemplate;

  try {
    const page = await getPage(id);
    return page;
  } catch (error) {
    console.error('Error fetching page', error);
    return undefined;
  }
};

/**
 * Generates a draft Page config either by cloning an existing page or creating a new one from default template.
 */
export async function generatePageDraftConfig({
  owner,
  existingPages,
  entityType,
  templateId,
  action,
  clientName,
}: GeneratePageDraftParams): Promise<Page> {
  const existingPageNames = getExistingPageNames(existingPages, entityType);

  const originalPage =
    action === 'clone' && templateId
      ? await getOriginalPage({ id: templateId, existingPages })
      : undefined;

  // Clone existing page
  if (originalPage) {
    return cloneAndSanitizePage(
      originalPage,
      owner,
      generateUniqueTitle(existingPageNames, originalPage.name, 'copy')
    );
  }

  // Brand-new page
  const baseConfig = getDefaultPageConfig(owner, entityType, clientName);
  baseConfig.name = generateUniqueTitle(existingPageNames, 'Untitled');

  return baseConfig;
}

export function cloneAndSanitizePage(
  originalPage: Page,
  owner: Owner,
  newName: string
): Page {
  const cloned = cloneDeep(originalPage);

  cloned.id = crypto.randomUUID();
  cloned.name = newName;
  cloned.owner = owner;

  // Remove Revelio-only unofficial template options
  cloned.tabs.forEach((tab) => {
    delete tab.tabContent.dateRangeDefault;
    tab.tabContent.secondaryFilters = [];
  });

  // Remove revelioPageId to mark as a custom page
  delete (cloned as Partial<Page>).revelioPageId;
  return cloned;
}

export function getExistingPageNames(
  existingPages: Page[],
  entityType?: EntityType
) {
  return existingPages
    .filter((p) => {
      if (entityType === 'report') return p.type === PageType.REPORT;
      const tabPrimary = p.tabs?.[0]?.tabContent?.primaryFilter?.id;
      return entityType
        ? tabPrimary === entityTypeToFilterId(entityType)
        : !tabPrimary;
    })
    .map((p) => p.name);
}

const createDefaultTabContent = ({
  primaryFilterId,
  selectionLimit = 6,
  selectionDefault = 6,
  isReport = false,
  clientName,
}: {
  primaryFilterId?: PrimaryFilterId;
  selectionLimit?: number;
  selectionDefault?: number;
  isReport?: boolean;
  clientName?: string;
}): TabContent => ({
  id: crypto.randomUUID(),
  items: isReport ? getReportDefaultItems(clientName) : [],
  ...(primaryFilterId && {
    primaryFilter: {
      id: primaryFilterId,
      selectionLimit,
      selectionDefault,
    },
  }),
  ...(isReport &&
    primaryFilterId && {
      reportParameters: [
        {
          parameterId: crypto.randomUUID(),
          filterId: primaryFilterId,
          isPrimary: true,
          name:
            primaryFilterId === FilterId.COMPANY
              ? 'Company'
              : toReadableFilterName(primaryFilterId),
        },
      ],
    }),
  grid: {
    rows: isReport
      ? DEFAULT_GRID_ROWS * 3 // title page, empty content page, footer page
      : DEFAULT_GRID_ROWS,
    columns: DEFAULT_GRID_COLUMNS,
  },
  secondaryFilters: [],
  ...(isReport && {
    title: '',
    description: '',
  }),
});

/** Minimal page config for global-assistant inline chart preview (no navigation to custom dash). */
export function buildGlobalAssistantChartPreviewPage(
  owner: Owner,
  entityType: EntityType,
  chartItem?: PositionedItem
): Page {
  const base = getDefaultPageConfig(owner, entityType, undefined);
  if (!chartItem) {
    return base;
  }
  const firstTab = base.tabs[0];
  if (!firstTab) {
    return base;
  }
  return {
    ...base,
    tabs: [
      {
        ...firstTab,
        tabContent: {
          ...firstTab.tabContent,
          items: [chartItem],
        },
      },
      ...base.tabs.slice(1),
    ],
  };
}

const getDefaultPageConfig = (
  owner: Owner,
  entityType?: EntityType,
  clientName?: string
): Page => ({
  id: crypto.randomUUID(),
  name: 'Untitled',
  type: entityType === 'report' ? PageType.REPORT : PageType.PRIVATE,
  owner,
  tabs: [
    {
      id: 'default',
      isActive: true,
      label: 'Default',
      tabContent: createDefaultTabContent(
        entityType === 'report'
          ? {
              primaryFilterId: FilterId.COMPANY,
              selectionLimit: 1,
              selectionDefault: 1,
              isReport: true,
              clientName,
            }
          : { primaryFilterId: entityTypeToFilterId(entityType) }
      ),
    },
  ],
});
